<template>
  <div class="page-default page-report">
    <div class="row m-0 page-section">
      <div
        class="col col-md-4 col-lg-3 section-left"
        :class="{ show: showSidebar }"
      >
        <div class="case-container">
          <div class="case-header text-capitalize text-dark">
            <div class="case-title text-truncate">Generate Report</div>
            <div
              class="case-header-icon clickable d-block d-md-none"
              @click="toggleSidebar()"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </div>
          </div>
          <div class="case-body py-3 px-4">
            <div class="form-group">
              <label for="selectDistrict" class="mb-1">District</label>
              <div class="input-group input-group-sm">
                <select
                  id="selectDistrict"
                  class="custom-select"
                  v-model="models.district"
                >
                  <option :value="null" disabled>-- Select district --</option>
                  <option
                    v-for="district in districts"
                    :key="district._id"
                    :value="district._id"
                  >
                    {{ district.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="selectDateFrom" class="mb-1">Report Type</label>
              <div class="input-group input-group-sm">
                <select
                  id="selectType"
                  class="custom-select"
                  v-model="models.type"
                >
                  <option :value="null" disabled>
                    -- Select report type --
                  </option>
                  <option value="RI">Routine Inspection</option>
                  <!-- <option value="R03">R03</option> -->
                  <option value="R01">R01</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="selectDateFrom" class="mb-1">Date Range</label>
              <v-date-picker
                class="inline-calendar"
                is-range
                v-model="models.date"
                mode="date"
              />
            </div>
            <div class="text-right">
              <button
                class="btn btn-primary btn-sm px-4"
                @click="generateReport"
                :disabled="disableApplyButton"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="col section-right text-truncate d-flex px-0 px-md-3">
        <div class="case-container bg-gradient w-100">
          <div
            class="section-header w-100"
            :class="{ 'shadow-none': cases.length < 1 }"
          >
            <button
              class="
                btn btn-outline-primary
                shadow-sm
                mr-3
                section-left-toggler
              "
              @click="toggleSidebar()"
            >
              <font-awesome-icon :icon="['fas', 'angle-right']" />
            </button>
            <p
              v-if="isInitial"
              class="
                font-weight-normal
                small
                mb-0
                font-italic
                flex-grow-1
                text-center
              "
            >
              Select date to generate report
            </p>
            <div
              class="d-flex justify-content-between flex-grow-1"
              v-else-if="cases.length > 0"
            >
              <div class="form-inline flex-grow-1">
                <div class="input-group flex-grow-1">
                  <div class="input-group-prepend">
                    <span class="input-group-text px-0 bg-white border-0">
                      <font-awesome-icon :icon="['fas', 'file-invoice']" />
                    </span>
                  </div>
                  <input
                    ref="reportNameInput"
                    type="text"
                    class="
                      form-control form-control-default form-control-sm
                      mb-0
                      border-0
                      flex-grow-1
                      font-weight-bold
                    "
                    placeholder="Enter report name here and hit enter"
                    v-model="models.title"
                    @keyup.enter="changeReportTitle"
                    @blur="changeReportTitle"
                  />
                </div>
              </div>
              <button
                class="btn btn-sm px-3 btn-primary rounded-pill btn-download"
                @click="downloadReport()"
                :disabled="!readyToPrint"
              >
                <font-awesome-icon :icon="['fas', 'file-download']" />
              </button>
            </div>
          </div>
          <div id="reportBody" class="report-body" v-if="!isInitial">
            <div
              class="report-item p-2 w-100 h-100 bg-darker"
              v-if="cases.length > 0"
            >
              <report-container
                :reportType="reportType"
                :cases="cases"
                :title="reportTitle"
                :district="selectedDistrict"
                @reportLoaded="changePrintState"
              />
            </div>
            <div
              class="
                font-weight-normal
                small
                mb-0
                font-italic
                flex-grow-1
                text-center
              "
              v-else
            >
              No report available on selected dates
            </div>
          </div>
        </div>
      </div>
    </div>
    <spinner v-if="isProcessing" />
  </div>
</template>

<script>
import Moment from "moment";
import API from "@/api";
import Spinner from "@/components/Spinner";
import ReportContainer from "@/components/ReportContainer";

export default {
  name: "reports",
  components: {
    Spinner,
    ReportContainer,
  },
  data() {
    return {
      isInitial: true,
      isProcessing: false,
      showSidebar: false,
      readyToPrint: false,
      cases: [],
      districts: [],
      reportTitle: null,
      reportType: null,
      models: {
        title: null,
        type: "RI",
        district: null,
        date: {
          start: null,
          end: null,
        },
      },
      selectedDistrict: "",
      userDistricts: [],
      userRole: null,
    };
  },
  computed: {
    disableApplyButton() {
      if (
        !this.models.type ||
        !this.models.district ||
        !this.models.date.start ||
        !this.models.date.end
      ) {
        return true;
      }

      return false;
    },
    reportDistrict() {
      const district = this.districts.find(
        (district) => district._id == this.models.district
      );

      return district ? district.name : "";
    },
  },
  methods: {
    formatDate(date) {
      if (!date) {
        return "";
      } else {
        return Moment(date).format("YYYY-MM-DD h:mm A");
      }
    },
    changePrintState(val) {
      this.readyToPrint = val;
    },
    changeReportTitle() {
      this.reportTitle = this.models.title;

      if (this.$refs.reportNameInput) {
        this.$refs.reportNameInput.blur();
      }
    },
    downloadReport() {
      if (!this.models.title) {
        return alert("Report name is not specified");
      } else {
        this.$nextTick(() => {
          document.title = `InfraJ - ${this.reportTitle}`;
          window.print();
          window.onafterprint = (e) => {
            document.title = "InfraJ";
          };
        });
      }
    },
    toggleSidebar() {
      this.showSidebar = !this.showSidebar;
    },
    async generateReport() {
      this.isInitial = true;
      this.isProcessing = true;
      this.showSidebar = false;

      // this.reportTitle = null;
      // this.models.title = null;

      let startDate = Moment(this.models.date.start).startOf("day").utc();
      let endDate = Moment(this.models.date.end).endOf("day").utc();

      let dateType = this.models.type == "R01" ? "solveDate" : "timestamp";

      startDate =
        this.models.type == "R01" ? startDate.unix() : startDate.toISOString();

      endDate =
        this.models.type == "R01" ? endDate.unix() : endDate.toISOString();

      this.reportType = this.models.type;

      let url = `cases?_limit=-1&_sort=createdAt&district=${
        this.models.district
      }&${dateType}_gte=${startDate}&${dateType}_lte=${endDate}&reportType=${
        this.models.type == "R01" ? "RI" : this.models.type
      }`;

      if (this.models.type == "R01") {
        url += "&status=solved";
      }

      API.get(url)
        .then((retVal) => {
          this.cases = retVal.data;

          this.selectedDistrict = this.reportDistrict;

          if (this.reportType) {
            let titleDitrict = this.districts.find(
              (district) => district._id == this.models.district
            );

            this.models.title =
              this.reportType == "RI"
                ? `LAPORAN LAWATAN INSPEKTOR RUTIN${
                    titleDitrict ? " - " + titleDitrict.name.toUpperCase() : ""
                  }`
                : "KERJA-KERJA PENYELENGARAAN RUTIN";

            this.changeReportTitle();
          }

          this.isInitial = false;
          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);

          this.isProcessing = false;
        });
    },
    async getDistricts() {
      this.isProcessing = true;

      let url = "districts?_sort=name";

      if (this.userRole == "Supervisor" && this.userDistricts.length > 0) {
        url = url
          .concat("&_id_in=")
          .concat(
            this.userDistricts.map((district) => district._id).join("&_id_in=")
          );
      }

      API.get(url)
        .then((retVal) => {
          this.districts = retVal.data;

          if (this.districts && this.districts.length == 1) {
            this.models.district = this.districts[0]._id;
          } else if (this.$route.query.district) {
            const queryDistrict = this.districts.find(
              (district) =>
                district.name.split(" ").join("_") == this.$route.query.district
            );

            if (queryDistrict) {
              this.models.district = queryDistrict._id;

              this.$router.replace({ query: {} });
            }
          }

          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);

          this.isProcessing = false;
        });
    },
  },
  mounted() {
    let _user = localStorage.getItem("user");

    if (_user) {
      _user = JSON.parse(_user);

      if (_user.districts && _user.districts.length > 0) {
        this.userDistricts = _user.districts.map((district) => {
          return {
            _id: district._id,
            name: district.name,
          };
        });
      }

      this.userRole = _user.role.name;
    }

    this.getDistricts();
  },
};
</script>